import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";
import {
	Box,
	CircularProgress,
	IconButton,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
	Button as BButton,
} from "@mui/material";

import { Add, Delete, Edit } from "@mui/icons-material";

import {
	DefaultParticipant,
	useDeleteDefaultParticipantMutation,
	useGetDefaultParticipantsQuery,
} from "@/pages/Private/redux/default-participants/default-participants.api";

import { formatDate } from "@/utils/formatDate";
import { Modal } from "@/components";

import {
	useGetCompanyForKpiQuery,
	useUpdateCompanyKpiMutation,
} from "@/pages/Private/redux/company/company.api";
import { useAppSelector } from "@/redux/hooks";

import { Checkbox } from "@/components/Checkbox/Checkbox";

import { ButtonColor } from "@/components/Button/types";

import { Button } from "@/components/Button/Button";

import { DefaultParticipantModal } from "./DefaultParticipantModal";
import { KpiDataDeleteModal } from "../../KpiDashboard/KPIDataDeleteModal";

export const CompanyGeneral: FunctionComponent = () => {
	const { t, i18n } = useTranslation();
	const ts = (key: string) => t(`defaultParticipants.${key}`);

	const { id } = useParams();
	const currentUser = useAppSelector((state) => state.auth?.user);

	// The page is addressed by company: /app/companies/:id. Without this the tab
	// would always render the signed-in user's own company (BCM360-334).
	const companyId = id ? +id : currentUser?.company?.id;

	const { data, isLoading } = useGetDefaultParticipantsQuery(companyId ?? skipToken);
	const [deleteParticipant, { isLoading: isDeleting }] = useDeleteDefaultParticipantMutation();
	const [modalOpen, setModalOpen] = useState(false);
	const [selectedParticipant, setSelectedParticipant] = useState<DefaultParticipant | null>(null);

	const [updateCompany, { isLoading: isUpdateLoading }] = useUpdateCompanyKpiMutation();

	const { data: companyData } = useGetCompanyForKpiQuery(companyId ?? -1, { skip: !companyId });

	const handleKpiToggle = async (val: boolean) => {
		try {
			await updateCompany({
				id: companyId ?? -1,
				kpiCollectionEnabled: val,
			}).unwrap();
		} catch (err) {
			console.error(err);
		}
	};
	const handleEdit = (participant: DefaultParticipant) => {
		setSelectedParticipant(participant);
		setModalOpen(true);
	};

	const handleCreate = () => {
		setSelectedParticipant(null);
		setModalOpen(true);
	};

	const handleClose = () => {
		setModalOpen(false);
		setSelectedParticipant(null);
	};

	const handleDelete = async (participantId: number) => {
		try {
			await deleteParticipant({ id: participantId, companyId }).unwrap();
		} catch (err) {
			console.error(err);
		}
	};

	const [isOpenWarningModal, setIsOpenWarningModal] = useState(false);
	const [wantToDeleteKPIModal, setWantToDeleteKPIModal] = useState(false);
	const [participantToDelete, setParticipantToDelete] = useState<number | null>(null);

	return (
		<div className="px-4 py-6">
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					justifyContent: "space-between",
					borderBottom: "1px solid",
					borderColor: "divider",
					pb: 6,
					mb: 6,
				}}
			>
				<Box sx={{ justifyContent: "space-between", display: "flex", mb: 6, alignItems: "center" }}>
					<div className="w-fit flex  flex-col gap-2">
						<p className="text-gray-800 text-[18px] font-bold ">{ts("title")}</p>
						<p className="text-gray-600 text-[14px] font-medium text-sm truncate w-max">
							{ts("description")}
						</p>
					</div>

					<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
						<BButton
							startIcon={<Add />}
							sx={{
								backgroundColor: "#B8431A",
								borderRadius: "8px",
								textTransform: "initial",
								color: "#fff",
								px: 2,
								py: 1,
								fontWeight: 600,
								fontSize: "14px",
								"&:hover": { backgroundColor: "#B8431A80" },
							}}
							variant="contained"
							onClick={handleCreate}
						>
							{ts("addParticipant")}
						</BButton>
					</Box>
				</Box>

				{isLoading ? (
					<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
						<CircularProgress size={24} />
					</Box>
				) : !data?.data?.length ? (
					<Box
						sx={{
							border: "1px dashed",
							borderColor: "divider",
							borderRadius: 2,
							py: 6,
							textAlign: "center",
						}}
					>
						<Typography color="text.secondary" variant="body2">
							{ts("noParticipants")}
						</Typography>
					</Box>
				) : (
					<TableContainer
						component={Paper}
						elevation={0}
						sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}
					>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>{t("basics.firstName")}</TableCell>
									<TableCell>{t("basics.lastName")}</TableCell>
									<TableCell>{ts("internalRole")}</TableCell>
									<TableCell>{ts("scenarioRoles")}</TableCell>
									<TableCell>{ts("lastUsed")}</TableCell>
									<TableCell align="right">{t("basics.actions")}</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{data.data.map((participant) => (
									<TableRow key={participant.id} hover>
										<TableCell className="capitalize">{participant.firstName}</TableCell>
										<TableCell className="capitalize">{participant.lastName}</TableCell>
										<TableCell className="capitalize">{participant.internalRoles}</TableCell>

										<TableCell className="capitalize">
											{t(`courses.progress.permissions.${participant.scenarioRoles}`)}
										</TableCell>
										<TableCell className="capitalize">
											{participant.lastUsedAt
												? formatDate(participant.lastUsedAt, ts, i18n.language)
												: ts("notUsed")}
										</TableCell>
										<TableCell align="right">
											<Stack direction="row" justifyContent="flex-end" spacing={1}>
												<Tooltip title={t("basics.edit")}>
													<IconButton size="small" onClick={() => handleEdit(participant)}>
														<Edit fontSize="small" />
													</IconButton>
												</Tooltip>
												<Tooltip title={t("basics.delete")}>
													<IconButton
														disabled={isDeleting}
														size="small"
														sx={{ color: "error.main" }}
														onClick={() => {
															const isRecentlyUsed =
																participant.lastUsedAt &&
																Date.now() - new Date(participant.lastUsedAt).getTime() <
																	30 * 24 * 60 * 60 * 1000;

															if (isRecentlyUsed) {
																setParticipantToDelete(participant.id);
																setIsOpenWarningModal(true);
															} else {
																handleDelete(participant.id);
															}
														}}
													>
														<Delete fontSize="small" />
													</IconButton>
												</Tooltip>
											</Stack>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}

				<Box sx={{ pt: 4, display: "flex", flexDirection: "column" }}>
					<div className="flex gap-2 flex-col">
						<p className="text-gray-800 text-[18px] font-bold ">
							{" "}
							{t("company.kpiCollection.title")}
						</p>
						<p className="text-gray-600 text-[15px] font-medium text-sm w-fit">
							{" "}
							{t("company.kpiCollection.description")}
						</p>
					</div>
					<div className="flex flex-row items-center mt-6">
						<div>
							<Checkbox
								bordered={true}
								isChecked={companyData?.kpiCollectionEnabled ?? false}
								isDisabled={isUpdateLoading}
								labelClass="mt-0"
								onChange={() => {
									handleKpiToggle(!companyData?.kpiCollectionEnabled);
								}}
							>
								<p className=" text-gray-800 text-[14px] font-medium text-sm truncate w-max">
									{t("company.kpiCollection.label")}
								</p>
							</Checkbox>

							<Button
								className="!h-9 !text-sm mt-4"
								color={ButtonColor.DANGER}
								title={t("kpiDashboard.deleteData")}
								onClick={() => {
									setWantToDeleteKPIModal(true);
								}}
							/>
						</div>
					</div>
				</Box>
			</Box>
			<DefaultParticipantModal
				companyId={companyId}
				isOpen={modalOpen}
				participant={selectedParticipant}
				onClose={handleClose}
			/>
			<Modal
				handleClose={() => {
					setIsOpenWarningModal(false);
					setParticipantToDelete(null);
				}}
				handleSave={() => {
					if (participantToDelete) {
						handleDelete(participantToDelete);
					}

					setIsOpenWarningModal(false);
					setParticipantToDelete(null);
				}}
				isLoading={isDeleting}
				isOpened={isOpenWarningModal}
				submitButtonColor="error"
				submitButtonText={t("basics.delete")}
				title={ts("deleteRecentlyUsedTitle")}
			>
				<Typography variant="body2">{ts("deleteRecentlyUsedWarning")}</Typography>
			</Modal>{" "}
			<KpiDataDeleteModal
				companyId={companyId}
				isOpen={wantToDeleteKPIModal}
				onClose={setWantToDeleteKPIModal}
			/>
		</div>
	);
};
